/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import { View, Text, StyleSheet, Platform, Dimensions, TouchableOpacity } from "react-native";
import { useSelector, useDispatch } from "react-redux";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import LHFilter from "../../../components/LHFilter";
import LHDataTable from "../../../components/LHDataTable";
import MobileFilterOverlay from "../../../components/MobileFilterOverlay";
import { createApiClient, apiClient } from "../../../api/apiClient";
import { getPeriodOptions, getWeekOptions, getYearOptions } from "../../../utils/filterOptions";
import {
  setLoading,
  setError,
  setFilteredTouchpoints,
  resetFilters,
  setTouchpoints,
  setSelectedFilters,
} from "../../../store/slices/nhoSlice";
import { handleNHOErrorWithFriendlyMessage, NHO_API_NAMES } from '../../../store/utils/nhoErrorHandler';
import TouchpointDetailsOverlay from "./TouchpointDetailsOverlay";
import FontFamily from "../../../constants/fonts";
import { 
  TOUCHPOINT_OPTIONS,
  TOUCHPOINT_DISPLAY_NAMES,
  convertProbationToMilestone,
  TABLE_TOUCHPOINT_OPTIONS
} from '../../../constants';
import { useTheme } from "../../../theme";

// Types
interface NHODashboardTableProps {
  onRowClick?: (rowData: any) => void;
  locationName?: string;
  showFilters?: boolean;
  showFilterModal?: boolean;
  setShowFilterModal?: (show: boolean) => void;
  tempFilters?: any;
  setTempFilters?: (filters: any) => void;
  isMobileIos?: boolean;
}

// Function to format email to name
const formatEmailToName = (email: string): string => {
  if (!email) {
    return "";
  }
  const namePart = email.split("@")[0];
  const names = namePart.split(".");
  return names
    .map((name) => name.charAt(0).toUpperCase() + name.slice(1))
    .join(" ")
    .replace(/Contractor$/, ""); // Remove "Contractor" suffix if present
};

// Function to get unique creators from touchpoints
const getUniqueCreators = (
  touchpoints: any[]
): Array<{ id: string; displayText: string }> => {
  const uniqueEmails = Array.from(
    new Set(touchpoints.map((tp) => tp.Created_By))
  );
  return uniqueEmails
    .filter((email) => email) // Filter out null/undefined
    .map((email) => ({
      id: email,
      displayText: formatEmailToName(email),
    }))
    .sort((a, b) => a.displayText.localeCompare(b.displayText));
};

// Column definition for the data table
const tableColumns = [
  { columnKey: "Employee_Name", header: "Employee Name" },
  { 
    columnKey: "Type_of_Touchpoint", 
    header: "Touch Point", 
    formatter: (value: string) => convertProbationToMilestone(value)
  },
  {
    columnKey: "Created",
    header: "Created Time",
    formatter: (value: string) => {
      if (!value) {
        return "N/A";
      }
      const date = new Date(value);
      const day = date.getDate();
      const month = date.toLocaleDateString("en-US", { month: "short" });
      const year = date.getFullYear();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? "PM" : "AM";
      const formattedHours = hours % 12 || 12;
      const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
      return `${month} ${day}, ${year}, ${formattedHours}:${formattedMinutes} ${ampm}`;
    },
  },
  {
    columnKey: "Created_By",
    header: "Created By",
    formatter: (value: string) => formatEmailToName(value),
  },
  { columnKey: "Period_Week", header: "Period Week" },
  { columnKey: "Status", header: "Status" },
];

// Function to get responsive items per row based on screen width
const getResponsiveItemsPerRow = (itemsPerRow: number): number => {
  const windowWidth = Dimensions.get("window").width;

  if (Platform.OS === "web") {
    return windowWidth > 1200
      ? Math.min(itemsPerRow, 4)
      : Math.min(itemsPerRow, 3);
  } else if (Platform.OS === "ios" || Platform.OS === "android") {
    if (windowWidth > 768) {
      return Math.min(itemsPerRow, 3); // iPad or large devices
    } else {
      return Math.min(itemsPerRow, 2); // Mobile devices
    }
  }
  return itemsPerRow;
};

const NHODashboardTable: React.FC<NHODashboardTableProps> = ({
  onRowClick,
  locationName,
  showFilters = true,
  showFilterModal: showFilterModalProp,
  setShowFilterModal: setShowFilterModalProp,
  tempFilters: tempFiltersProp,
  setTempFilters: setTempFiltersProp,
  isMobileIos: isMobileIosProp,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  // Local state for data display
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize] = useState(200); // Increased page size to 200
  const [currentPage, setCurrentPage] = useState(1); // Add missing currentPage state
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  // Add state to manage overlay visibility and data
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlayData, setOverlayData] = useState(null);
  
  // Use props if provided, otherwise use local state (for backward compatibility)
  const [localShowFilterModal, setLocalShowFilterModal] = useState(false);
  const [localTempFilters, setLocalTempFilters] = useState<any>({});
  const [localIsMobileIos, setLocalIsMobileIos] = useState(false);

  const showFilterModal = showFilterModalProp ?? localShowFilterModal;
  const setShowFilterModal = setShowFilterModalProp ?? setLocalShowFilterModal;
  const tempFilters = tempFiltersProp ?? localTempFilters;
  const setTempFilters = setTempFiltersProp ?? setLocalTempFilters;
  
  // Compute iOS mobile boolean if not provided
  useEffect(() => {
    if (isMobileIosProp === undefined) {
      const windowWidth = Dimensions.get("window").width;
      setLocalIsMobileIos(Platform.OS === "ios" && windowWidth < 768);
    }
  }, [isMobileIosProp]);
  
  const isMobileIos = isMobileIosProp ?? localIsMobileIos;

  // Access Redux state and dispatch
  const dispatch = useDispatch();
  const {
    loading,
    error,
    touchpoints = [],
    filteredTouchpoints = [],
    selectedFilters,
    employees: nhoEmployees = [],
  } = useSelector((state: any) => state.nho);
  const {
    selectedLocation,
    employees: { data: appEmployees = [] } = { data: [] },
    periodWeek: { data: periodWeekData = {} } = { data: {} },
  } = useSelector((state: any) => state.appData);

  // Prepare filter options
  const touchPointOptions = TABLE_TOUCHPOINT_OPTIONS;

  // Get employee data from nho slice instead of appData
  const employeeOptions = useSelector((state: any) => {
    const employees = state.nho.employees || [];
    
    return employees.length > 0
      ? employees.map((emp: any) => ({
          id: emp.GPID || "",
          displayText:
            emp.Full_Name || `${emp.First_Name || ""} ${emp.Last_Name || ""}`,
        }))
      : [];
  });

  // Define filter options for LHFilter component
  const actionOptions = [
    { id: "In Progress", displayText: "In Progress" },
    { id: "Completed", displayText: "Completed" },
  ];

  const filterOptions = [
    {
      key: "employee",
      label: "Select an Employee",
      selectedOptionId: selectedFilters?.employeeGpid || "",
      options: employeeOptions,
    },
    {
      key: "touchpoint",
      label: "Type of Touch Point",
      selectedOptionId: selectedFilters?.touchpoint || "",
      options: touchPointOptions,
    },
    {
      key: "period",
      label: "Period",
      selectedOptionId: selectedFilters?.period || "",
      options: getPeriodOptions(),
    },
    {
      key: "Week",
      label: "Week",
      selectedOptionId: selectedFilters?.Week || "",
      options: getWeekOptions(),
    },
    {
      key: "year",
      label: "Year",
      selectedOptionId: selectedFilters?.year || "",
      options: getYearOptions(),
    },
    {
      key: "createdBy",
      label: "Created By",
      selectedOptionId: selectedFilters?.createdBy || "",
      options: getUniqueCreators(touchpoints),
    },
    {
      key: "Action",
      label: "Status",
      selectedOptionId: selectedFilters?.status || "",
      options: actionOptions,
    },
  ];
  // Function to fetch touchpoint data
  const fetchTouchpoints = async (applyFilters = false) => {
    const location = selectedLocation?.displayText || locationName || "";
    console.log("Location in fetchTouchPoints");
    console.log(location);

    if (!location) {
      try {
        handleNHOErrorWithFriendlyMessage(NHO_API_NAMES.FETCH_NHO, new Error('No location selected'), true, { location });
      } catch (e) {
        console.error('Error while dispatching NHO centralized handler for missing location:', e);
      }
      return;
    }

    dispatch(setLoading(true));

    try {
      // Create API client instance
      const api = createApiClient();

      // Build URL with parameters
      let url = `/new-hire-onboarding?location=${encodeURIComponent(location)}`;
      // Add optional filters if applying filters
      if (applyFilters) {
        if (selectedFilters.employee) {
          url += `&gpid=${selectedFilters.employeeGpid}`;
        }
        if (selectedFilters.touchpoint) {
          url += `&touchpoint=${selectedFilters.touchpoint}`;
        }
        if (selectedFilters.periodWeek) {
          url += `&period=${selectedFilters.periodWeek}`;
        }
      }

      // Add page size parameter
      url += "&pageSize=20";

      // Make API call
      const response = await apiClient(api, url, "GET");

      // Set total items for pagination
      setTotalItems(response.totalItems || 0);

      // Transform the response data to ensure the 'type' property is included
      const transformedData = (response.data || []).map((item: any) => ({
        ...item,
        type: item.Type_of_Touchpoint || "unknown", // Map 'Type_of_Touchpoint' to 'type'
        GPID: item.GPID,
        // Removing padding of GPID to preserve original value for filtering and API calls, can add a separate formatted name field if needed for display
        //GPID: item.GPID ? String(item.GPID).padStart(8, '0') : item.GPID,

      }));

      // Store the transformed touchpoints in Redux store
      dispatch(setTouchpoints(transformedData));

      // Also set filtered touchpoints to the same transformed data
      dispatch(setFilteredTouchpoints(transformedData));

      dispatch(setError(null));
    } catch (err) {
      console.error("Error fetching touchpoints data:", err);
      try {
        handleNHOErrorWithFriendlyMessage(NHO_API_NAMES.FETCH_NHO, err, true, { location });
      } catch (e) {
        console.error('Error while dispatching NHO centralized handler:', e);
      }
    } finally {
      dispatch(setLoading(false));
    }
  };
  // Update fetchTouchpointsWithFilters to handle empty or missing filters
  // Allow passing an optional filters object so callers (like handleFilterChange)
  // can trigger an immediate fetch when a filter changes.
  const fetchTouchpointsWithFilters = async (filtersOverride?: any) => {
    const filtersToUse = filtersOverride || selectedFilters || {};
    const location = selectedLocation?.displayText || locationName || "";
    if (!location) {
      try {
        handleNHOErrorWithFriendlyMessage(NHO_API_NAMES.FETCH_NHO, new Error('No location selected'), true, { location, filters: filtersToUse });
      } catch (e) {
        console.error('Error while dispatching NHO centralized handler for missing location (filters):', e);
      }
      return;
    }

    dispatch(setLoading(true));

    try {
      const api = createApiClient();
      let url = `/new-hire-onboarding?location=${encodeURIComponent(location)}`;

      // Add filters to the URL if they have valid values
      if (filtersToUse.employeeGpid) {
        url += `&gpid=${filtersToUse.employeeGpid}`;
      }
      if (filtersToUse.touchpoint) {
        url += `&touchpoint=${filtersToUse.touchpoint}`;
      }
      if (filtersToUse.period) {
        url += `&period=${filtersToUse.period}`;
      }
      if (filtersToUse.Week) {
        url += `&week=${filtersToUse.Week}`;
      }
      if (filtersToUse.year) {
        url += `&year=${filtersToUse.year}`;
      }
      if (filtersToUse.createdBy) {
        url += `&createdBy=${encodeURIComponent(filtersToUse.createdBy)}`;
      }
      if (filtersToUse.status && filtersToUse.status !== "") {
        url += `&status=${encodeURIComponent(filtersToUse.status)}`;
      }

      // Log the final URL for debugging
      console.log("API Request URL:", url);

      const response = await apiClient(api, url, "GET");

      setTotalItems(response.totalItems || 0);

      const transformedData = (response.data || []).map((item: any) => ({
        ...item,
        type: item.Type_of_Touchpoint || "unknown",
      }));

      dispatch(setTouchpoints(transformedData));
      dispatch(setFilteredTouchpoints(transformedData));
      dispatch(setError(null));
    } catch (err) {
      console.error("Error fetching touchpoints data with filters:", err);
      try {
        handleNHOErrorWithFriendlyMessage(NHO_API_NAMES.FETCH_NHO, err, true, { location, filters: filtersToUse });
      } catch (e) {
        console.error('Error while dispatching NHO centralized handler:', e);
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Effect to fetch data when component mounts or location changes
  useEffect(() => {
    console.log("selectedLocation in NHO", selectedLocation);
    fetchTouchpoints();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationName, selectedLocation, pageSize]);

  // Sync tempFilters with selectedFilters when modal opens (following ManageEmployeeUpdates pattern)
  useEffect(() => {
    if (showFilterModal) {
      setTempFilters(selectedFilters || {});
    }
  }, [showFilterModal, selectedFilters]);

  // Mobile filter handlers (following ManageEmployeeUpdates pattern)
  const handleClose = () => {
    setShowFilterModal(false);
    setTempFilters({});
  };

  const handleTempFilterChange = (key: string, value: any) => {
    setTempFilters((prev: any) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Special handler for MobileFilterOverlay that receives all filters at once
  // Following ManageEmployeeUpdates pattern: batch update filters, then apply
  const handleMobileProceed = (selectedFiltersFromModal: Partial<Record<string, any>>) => {
    console.log("[NHO Mobile Filter] Proceeding with filters:", selectedFiltersFromModal);

    // Build the payload for Redux
    const payload: any = {};

    Object.keys(selectedFiltersFromModal).forEach((key) => {
      const value = selectedFiltersFromModal[key];
      
      if (key === "employee" && value) {
        // Handle employee filter
        const employeeData = appEmployees.find(
          (emp: any) => String(emp.GPID) === value.id || emp.Full_Name === value.displayText
        );
        if (employeeData) {
          payload.employee = employeeData.Full_Name || value.displayText || "";
          payload.employeeFirstName = employeeData.First_Name || "";
          payload.employeeLastName = employeeData.Last_Name || "";
          payload.employeeGpid = employeeData.GPID || "";
        } else {
          payload.employee = value.displayText || "";
          payload.employeeGpid = value.id || "";
        }
      } else if (key === "createdBy" && value) {
        // For Created By filter, store the email (id)
        payload.createdBy = value.id || value;
      } else if (key === "Action" && value) {
        // For Action/Status filter, map to status
        const statusValue = value.displayText === "All" ? "" : value.displayText;
        payload.status = statusValue;
      } else if (value && value.id !== undefined) {
        // For other filters with id
        payload[key] = value.id || "";
      } else if (value) {
        // Direct value
        payload[key] = value;
      }
    });

    // Dispatch to Redux
    dispatch(setSelectedFilters(payload));

    // Immediately apply filters
    fetchTouchpointsWithFilters(payload);

    // Close modal
    setShowFilterModal(false);
    setTempFilters({});
  };

  // Handle filter change
  const handleFilterChange = (key: string, value: any) => {
    // Handle both single and array values
    const selectedItem = Array.isArray(value) ? value[0] : value;

    if (!selectedItem) {
      // If clearing a filter, update Redux with empty values and auto-apply
      if (key === "Action") {
        // 'Action' maps to a status override for fetching but is not part of the nho selectedFilters shape
        const updatedFilters = { ...selectedFilters, status: "" };
        // Immediately apply filters with cleared status
        fetchTouchpointsWithFilters(updatedFilters);
        return;
      }

      const clearPayload: any = { [key]: "" };
      if (key === "employee") {
        clearPayload.employeeGpid = "";
      }
      dispatch(setSelectedFilters(clearPayload));
      // Immediately apply filters
      fetchTouchpointsWithFilters({ ...selectedFilters, ...clearPayload });
      return;
    }

    if (key === "employee") {
      // Find the full employee data from nho employees list
      const employeeData = nhoEmployees.find(
        (emp: any) => String(emp.GPID) === String(selectedItem.id)
      );
      const payload = {
        employee:
          employeeData?.Full_Name ||
          `${employeeData?.First_Name || ""} ${employeeData?.Last_Name || ""}`.trim() ||
          selectedItem.displayText ||
          "",
        employeeFirstName: employeeData?.First_Name || "",
        employeeLastName: employeeData?.Last_Name || "",
        employeeGpid: String(selectedItem.id) || "",
      };
      dispatch(setSelectedFilters(payload));
      fetchTouchpointsWithFilters({ ...selectedFilters, ...payload });
    } else if (key === "createdBy") {
      // For Created By filter, only store the email (id) since that's what the API expects
      const payload = { [key]: selectedItem.id };
      dispatch(setSelectedFilters(payload));
      fetchTouchpointsWithFilters({ ...selectedFilters, ...payload });
    } else if (key === "Action") {
      // For Action filter, map displayText to a status override and immediately fetch.
      const statusValue = selectedItem.displayText === "All" ? "" : selectedItem.displayText;
      // Don't dispatch status to setSelectedFilters because nhoSlice.selectedFilters doesn't include 'status'
      fetchTouchpointsWithFilters({ ...selectedFilters, status: statusValue });
    } else {
      // For other filter types
      const payload = { [key]: selectedItem.id || "" };
      dispatch(setSelectedFilters(payload));
      fetchTouchpointsWithFilters({ ...selectedFilters, ...payload });
    }
  };

  // Update applyFilters function to ensure all filters are applied
  const applyFilters = () => {
    // Log selected filters for debugging
    console.log("Applying filters:", selectedFilters);

    // Filter the touchpoints by Action (status) if selected
    let filtered = [...touchpoints];
    if (selectedFilters.status && selectedFilters.status !== "") {
      filtered = filtered.filter(
        (tp: any) => tp.Status === selectedFilters.status
      );
    }
    // Ensure all filters are passed to the fetch function
    fetchTouchpointsWithFilters();
    // Also update filteredTouchpoints in Redux for local filtering
    dispatch(setFilteredTouchpoints(filtered));
  };

  // Reset filters function
  const handleResetFilters = () => {
    // Reset Redux filters
    dispatch(resetFilters());
    // Fetch data without filters
    fetchTouchpoints();
    // Reset pagination
    setCurrentPage(1);
  };

  // Function to handle row click and fetch data
  const handleRowClick = async (rowData: any) => {
    console.log("Row clicked:", rowData); // Debugging log

    // Check if Employee_Name cell was clicked
    if (rowData.columnKey === "Employee_Name") {
      console.log("Employee Name cell clicked, handling navigation");

      // Get GPID from row data or fallback to PPID
      const gpid = rowData.GPID || rowData.PPID;

      if (!gpid) {
        console.error("No GPID/PPID found in row data:", rowData);
        dispatch(setError("Employee ID not found"));
        return;
      }

      // Map Type_of_Touchpoint to observationType
      const typeOfTouchpoint = rowData.Type_of_Touchpoint;
      let observationType = "";

      if (typeOfTouchpoint === "Pulse Check") {
        observationType = "pulse_check";
      } else if (typeOfTouchpoint === "Probation 30" || typeOfTouchpoint === "Milestone 1") {
        observationType = "30";
      } else if (typeOfTouchpoint === "Probation 60" || typeOfTouchpoint === "Milestone 2") {
        observationType = "60";
      } else if (typeOfTouchpoint === "Probation 90" || typeOfTouchpoint === "Milestone 3") {
        observationType = "90";
      } else if (typeOfTouchpoint === "Preferred Method") {
        observationType = "preferred_method";
      } else {
        console.warn("Unknown touchpoint type:", typeOfTouchpoint);
        observationType = typeOfTouchpoint || "unknown";
      }

      // Resolve employee name from nho employees list
      const employeeRecord = nhoEmployees.find(
        (emp: any) => String(emp.GPID) === String(gpid)
      );
      const employeeFullName =
        employeeRecord?.Full_Name ||
        `${employeeRecord?.First_Name || ""} ${employeeRecord?.Last_Name || ""}`.trim() ||
        rowData.Employee_Name ||
        "";

      console.log("Setting Redux filters:", {
        employee: employeeFullName,
        employeeGpid: gpid,
        observationType: observationType,
      });

      // Set Redux store filters
      dispatch(
        setSelectedFilters({
          employee: employeeFullName,
          employeeFirstName: employeeRecord?.First_Name || "",
          employeeLastName: employeeRecord?.Last_Name || "",
          employeeGpid: gpid,
          observationType: observationType,
          touchpoint: typeOfTouchpoint,
          touchpointId: rowData.Id ?? undefined,
          periodWeek: rowData.Period_Week || "",
          status: rowData.Status || "", // Include status for org context exclusion logic
        })
      );

      // Determine navigation destination based on touchpoint type
      let destination = "";
      if (observationType === "pulse_check") {
        destination = "PulseCheckTouchPoint";
      } else if (
        ["30", "60", "90", "preferred_method"].includes(observationType)
      ) {
        destination = "SpecificPeriodTouchPoint";
      }

      // Call navigation function if available
      if (destination && onRowClick) {
        console.log(`Calling onRowClick for navigation to: ${destination}`);
        onRowClick({
          shouldNavigateToTouchpoint: true,
          destination,
          filters: {
            employee: gpid,
            observationType: observationType,
          },
        });
      } else {
        console.warn("No destination determined or onRowClick not available", {
          destination,
          onRowClick: !!onRowClick,
        });
      }

      return; // Exit early for Employee_Name clicks
    }

    // Handle regular cell clicks (non-Employee_Name) - show overlay
    const rowId = rowData.id || rowData.Id;
    if (!rowId) {
      console.error("Row data is invalid or missing ID:", rowData);
      return;
    }

    dispatch(setLoading(true));
    console.log("Fetching data for ID:", rowId); // Log before API call
    try {
      const api = createApiClient();
      const url = `/new-hire-onboarding/${rowId}`;
      const response = await apiClient(api, url, "GET");

      console.log("API response:", response);

      const typeOfTouchpoint =
        response.probation?.type_of_touchpoint ||
        response.user?.type ||
        rowData.Type_of_Touchpoint ||
        "unknown";

      const transformedResponse = {
        ...response,
        type: typeOfTouchpoint,
        metrics: response.metrics || [], // Include metrics data
      };

      console.log("Transformed response:", transformedResponse);

      if (transformedResponse) {
        setOverlayData(transformedResponse);
        setOverlayVisible(true);
      } else {
        console.error("API response is empty or undefined:", response);
      }
    } catch (err) {
      console.error("Error during API call:", err);
      try {
        handleNHOErrorWithFriendlyMessage(NHO_API_NAMES.FETCH_NHO_BY_ID, err, true, { rowId });
      } catch (e) {
        console.error('Error while dispatching NHO centralized handler for touchpoint details:', e);
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Note: Using LHDataTable's internal pagination
  return (
    <View
      style={[
        styles.container,
        isDarkTheme && { backgroundColor: colors.background.surface },
      ]}
    >
      {/* Filter Section - Hidden on iPhone */}
      {!isMobileIos && (
        <View style={styles.filterContainer} testID="filter-container">
          {/* Custom filter component with callback to track visibility */}
          <LHFilter
            filters={filterOptions}
            onFilterChange={handleFilterChange}
            itemsPerRow={getResponsiveItemsPerRow(3)}
            showActionButton={false}
            onClearAllFilters={handleResetFilters}
          />

          {/* Show buttons only when filters are visible and showFilters prop is true */}
        </View>
      )}
      {/* Show loading or error messages if applicable */}
      {loading && (
        <View style={styles.messageContainer}>
          <Text
            style={[
              styles.loadingText,
              isDarkTheme && { color: colors.text.primary },
            ]}
          >
            Loading nho touchpoint data...
          </Text>
        </View>
      )}
      {error && (
        <View style={styles.messageContainer}>
          <Text
            style={[
              styles.errorText,
              isDarkTheme && { color: colors.text.primary },
            ]}
          >
            Error: {error}
          </Text>
        </View>
      )}
      {/* Data Table Section */}
      <View
        style={[
          styles.dataTableContainer,
          isDarkTheme && {
            backgroundColor: colors.background.elevated,
            borderColor: colors.border.default,
            borderWidth: 1,
          },
        ]}
      >
        <LHDataTable
          data={filteredTouchpoints}
          columns={tableColumns}
          tableHeader={"New Hire Touchpoints"}
          tableDescription={`Showing ${filteredTouchpoints.length} of ${totalItems} records`}
          onRowClick={handleRowClick}
          maxRecordsToShow={10}
          highContrastText={isDarkTheme}
        />
      </View>
      {/* Touchpoint Details Overlay */}
      <TouchpointDetailsOverlay
        visible={overlayVisible}
        onDismiss={() => setOverlayVisible(false)}
        touchpoint={overlayData}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#ffffff",
    //borderRadius: 8,
    padding: 16,
    //marginTop: 16,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  messageContainer: {
    padding: 16,
    alignItems: "center",
  },
  filterContainer: {
    marginBottom: 0,
  },
  loadingText: {
    fontSize: 16,
    color: "#0066CC",
  },
  errorText: {
    fontSize: 16,
    color: "#DC3545",
  },
  dataTableContainer: {
    // ...Platform.select({
    //   ios: {
    //     shadowColor: "#0066CC",
    //     shadowOffset: { width: 0, height: 2 },
    //     shadowOpacity: 0.3,
    //     shadowRadius: 6,
    //   },
    //   android: {
    //     elevation: 8,
    //     shadowColor: "#0066CC",
    //   },
    //   web: {
    //     boxShadow: "0 4px 12px 0 rgba(0, 102, 204, 0.4)",
    //   },
    // }),
    backgroundColor: "#fff",
    borderRadius: 8,
    marginTop: 16,
  },
  actionButtonsContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 16,
  },
  actionButton: {
    width:
      Dimensions.get("window").width < 400
        ? 90
        : Platform.OS === "web"
        ? "10%"
        : 120,
    minWidth: Platform.OS === "web" ? 120 : 60,
    fontSize: Dimensions.get("window").width < 400 ? 12 : 16,
  },
  mobileHeader: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    marginBottom: 16,
    paddingHorizontal: 4,
  },
  mobileHeaderText: {
    fontSize: 20,
    fontWeight: "700",
    color: "#2D2D2D",
    fontFamily: FontFamily.bold,
  },
  mobileFilterButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#005CBC",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 24,
    gap: 4,
  },
  mobileFilterButtonText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#FFFFFF",
    fontFamily: FontFamily.regular,
  },
});

export default NHODashboardTable;
